import { useEffect, useMemo } from 'react'
import {
  IProduct,
  IProductSearch,
  ProductPromotionType,
} from '@core/api/Products/types'
import { LYSDivider, LYSTypography } from '@core/components/Primitives'
import { useRelatedProducts } from '@core/components/ProductDetail/useRelatedProducts'
import PromotionContext from '@core/components/shared/PromotionContext'
import { productCollectionViewEvent } from '@core/events/product'
import useCart from '@core/hooks/cart/useCart'
import { useTranslation } from '@core/i18n/i18n'
import { useServices } from '@core/utils/ioc'
import { useProductDetailContext } from '@core/utils/models/product/ProductContext'
import { usePageContext } from '@core/utils/pages/PageContext'
import ProductBundleItem from './ProductBundleItem'
import ProductBundleSummaryRow from './ProductBundleSummaryRow'
import style from './index.module.less'
import useBundleItemSelection from './useBundleItemSelection'

interface Props {
  className: string
  withTopDivider?: boolean
}

const useBundledProducts = (product?: IProduct) => {
  const { relatedProducts } = useRelatedProducts(product, 'bundle')
  return useMemo(() => {
    // Display only purchasable products
    if (!product || !relatedProducts.length) {
      return []
    }
    return [
      product as unknown as IProductSearch,
      ...relatedProducts.filter(
        (p) => p.mainVariant.packagingUnits[0].purchasable
      ),
    ]
  }, [relatedProducts, product])
}

const ProductBundle: React.FC<Props> = ({ className, withTopDivider }) => {
  const { t } = useTranslation()
  const { eventBus } = useServices()
  const { addBundleItems, isCartUpdatePending } = useCart()
  const { product } = useProductDetailContext() || {}
  const pageContext = usePageContext()
  const productBundle = useBundledProducts(product)

  const { selectedBundleItems, isSelected, select, productBundlePrice } =
    useBundleItemSelection(productBundle)

  useEffect(() => {
    if (productBundle?.length) {
      eventBus.publish(
        productCollectionViewEvent(
          productBundle,
          {
            isPromotion: true,
            promotionType: ProductPromotionType.Bundle,
          },
          pageContext
        )
      )
    }
  }, [eventBus, productBundle, pageContext])

  if (
    !product ||
    !productBundle ||
    productBundle.length < 1 ||
    product.mainVariant?.packagingUnits[0].purchasable === false
  )
    return null

  const handleAddToCart = () => {
    addBundleItems(selectedBundleItems)
  }

  return (
    <PromotionContext.Provider
      value={{
        isPromotion: true,
        promotionType: ProductPromotionType.Bundle,
      }}
    >
      {withTopDivider && <LYSDivider />}
      <div className={className}>
        <LYSTypography.Title level={3} className={style.title}>
          {t('productDetail.productBundleTitle')}
        </LYSTypography.Title>

        {productBundle.map((product, index) => (
          <ProductBundleItem
            product={product}
            isSelected={isSelected(product)}
            onSelectionChange={select}
            key={product.id}
            isLastItem={index === productBundle.length - 1}
          />
        ))}
        <ProductBundleSummaryRow
          onAddToCart={handleAddToCart}
          disabled={selectedBundleItems.length === 0 || isCartUpdatePending}
          productBundlePrice={productBundlePrice}
        />
      </div>
    </PromotionContext.Provider>
  )
}

export default ProductBundle
